// components/PickerField.jsx
'use client';
import { useMemo, useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextField } from '@mui/material';
import SelectListDialog from './SelectListDialog';

export default function PickerField({
  value,
  selectedItems = [],
  options = [],
  onChange,
  onSearch,
  onLoadMore,
  isLoading = false,
  label = 'Select',
  placeholder = '',
  allowAdd = false,
  AddComponent = null,
  getOptionId = (o) => (o == null ? o : (o.id ?? o.value ?? o)),
  getOptionLabel = (o) => (o == null ? '' : (o.label ?? o.name ?? String(o))),
  getOptionSecondaryLabel = (o) => (o == null ? '' : (o.secondaryLabel ?? o.companyName ?? '')),
  getOptionUnit = (o) => (o == null ? '' : (o.unit ?? '')),
  getOptionDisabled = () => false,
  getDisplayLabel,
  dialogTitle = null,
  disabled = false,
  helperText = '',
  dialogProps = {},
  addDialogProps = {},
  searchKey = 'name',
  searchPlaceholder,
  infoMessage = null,
  required = false,
  error = false,
  multiSelect = false,
  pageSize = 10,
  pageSizeOptions,
  onPageSizeChange,
  listOptions,
  page,
  onPageChange,
  totalRecord
}) {
  const [open, setOpen] = useState(false);

  const [cachedLabels, setCachedLabels] = useState({});

  const resolveLabel = useCallback(
    (found) => {
      if (!found) return '';
      let l = getDisplayLabel ? getDisplayLabel(found) : getOptionLabel(found);
      // If the resolved label is just the ID, try to find a real string label
      if (l === String(found.value) || l === String(found.id) || !l) {
        l =
          found.label ??
          found.license_plate ??
          found.name ??
          found.raw?.name ??
          found.raw?.license_plate ??
          found.original?.license_plate ??
          l;
      }
      return l;
    },
    [getDisplayLabel, getOptionLabel]
  );

  // Passively cache all incoming options so we remember their labels even if options are cleared
  useEffect(() => {
    if (!options || options.length === 0) return;

    setCachedLabels((prev) => {
      let hasChanges = false;
      const next = { ...prev };
      options.forEach((o) => {
        const id = String(getOptionId(o));
        const l = resolveLabel(o);
        if (l && l !== id && next[id] !== l) {
          next[id] = l;
          hasChanges = true;
        }
      });
      return hasChanges ? next : prev;
    });
  }, [options, getOptionId, resolveLabel]);

  const selectedLabel = useMemo(() => {
    if (multiSelect && Array.isArray(value)) {
      if (value.length === 0) return '';
      const labels = value.map((val) => {
        const found =
          (listOptions || []).find((o) => String(getOptionId(o)) === String(val)) ||
          (options || []).find((o) => String(getOptionId(o)) === String(val)) ||
          (selectedItems || []).find((o) => String(getOptionId(o)) === String(val));

        if (found) {
          return resolveLabel(found);
        }

        return cachedLabels[String(val)] || val;
      });
      return labels.filter(Boolean).join(', ');
    }

    const found =
      (listOptions || []).find((o) => String(getOptionId(o)) === String(value)) ||
      (options || []).find((o) => String(getOptionId(o)) === String(value)) ||
      (selectedItems || []).find((o) => String(getOptionId(o)) === String(value));

    if (found) {
      return resolveLabel(found);
    }

    return cachedLabels[String(value)] || value;
  }, [options, listOptions, selectedItems, getOptionId, value, multiSelect, cachedLabels, resolveLabel]);

  return (
    <>
      <TextField
        fullWidth
        label={label}
        value={selectedLabel || ''}
        placeholder={placeholder}
        onClick={() => !disabled && setOpen(true)}
        helperText={helperText}
        disabled={disabled}
        inputpropsprops={{}}
        endadornment={null}
        slotProps={{
          input: {
            readOnly: true
          }
        }}
        required={required}
        error={error}
      />

      <SelectListDialog
        open={open}
        onClose={() => setOpen(false)}
        title={dialogTitle ?? label}
        options={listOptions || options}
        value={value}
        onSearch={onSearch}
        onLoadMore={onLoadMore}
        searchKey={searchKey}
        searchPlaceholder={searchPlaceholder}
        isLoading={isLoading}
        onSelect={(v) => {
          onChange && onChange(v);
        }}
        allowAdd={allowAdd}
        AddComponent={AddComponent}
        onAddSuccess={(created) => {
          if (created == null) return;
          const id = (created && (created.id ?? created.value)) ?? created;
          onChange && onChange(id);
        }}
        getOptionId={getOptionId}
        getOptionLabel={getOptionLabel}
        getOptionSecondaryLabel={getOptionSecondaryLabel}
        getOptionUnit={getOptionUnit}
        getOptionDisabled={getOptionDisabled}
        dialogProps={dialogProps}
        addDialogProps={addDialogProps}
        multiSelect={multiSelect}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        onPageSizeChange={onPageSizeChange}
        page={page}
        onPageChange={onPageChange}
        totalRecord={totalRecord}
        infoMessage={infoMessage}
      />
    </>
  );
}

PickerField.propTypes = {
  value: PropTypes.any,
  options: PropTypes.array,
  onChange: PropTypes.func,
  onSearch: PropTypes.func,
  isLoading: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  infoMessage: PropTypes.node,
  allowAdd: PropTypes.bool,
  AddComponent: PropTypes.any,
  getOptionId: PropTypes.func,
  getOptionLabel: PropTypes.func,
  getOptionSecondaryLabel: PropTypes.func,
  getOptionUnit: PropTypes.func,
  dialogTitle: PropTypes.string,
  disabled: PropTypes.bool,
  helperText: PropTypes.string,
  dialogProps: PropTypes.object,
  addDialogProps: PropTypes.object,
  multiSelect: PropTypes.bool
};
